import React, { useState, useEffect } from 'react';
import { Container, Button, Alert, Badge } from 'reactstrap';
import { useDispatch, useSelector } from 'react-redux';

import {
  getLeads,
  updateLeadOrder,
  exportLeadsCsv,
  updateLead,
} from '../../redux/actions/lead';
import LeadDetail from './subcomponents/LeadDetail';
import NewLeadForm from './subcomponents/NewLeadForm';
import AssignedForm from './subcomponents/AssignedForm';
import NegociationForm from './subcomponents/NegociationForm';
import VisitForm from './subcomponents/VisitForm';
import CloseForm from './subcomponents/CloseForm';
import CompletedForm from './subcomponents/CompletedForm';
import Loading from '../../components/Loading/Loading';
import styles from './styles.module.scss';
import { STATE_OPTIONS } from './constants';
import { dateFormat } from '../../utils/utils';
import { hasPermission } from '../../utils/permissions';

function LeadBoard() {
  const dispatch = useDispatch();
  const leadState = useSelector((state) => state.lead.get);
  const updateOrderState = useSelector((state) => state.lead.updateOrder);
  const updateLeadState = useSelector((state) => state.lead.update);
  const [data, setData] = useState([]);
  const [originalData, setOriginalData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [backgroundRefreshing, setBackgroundRefreshing] = useState(false);
  const [error, setError] = useState(null);
  const [draggedCard, setDraggedCard] = useState(null);
  const [dragOverInfo, setDragOverInfo] = useState(null);
  const [leadToAssign, setLeadToAssign] = useState(null);
  const [leadOrder, setLeadOrder] = useState(null);
  const [leadToDetail, setLeadToDetail] = useState(false);
  const [exportingCsv, setExportingCsv] = useState(false);
  const [newLeadForm, setNewLeadForm] = useState(false);
  const [assignedForm, setAssignedForm] = useState(false);
  const [negociationForm, setNegociationForm] = useState(false);
  const [visitForm, setVisitForm] = useState(false);
  const [closeForm, setCloseForm] = useState(false);
  const [completedForm, setCompletedForm] = useState(false);
  const [columnDragOver, setColumnDragOver] = useState(null);
  const [dropPosition, setDropPosition] = useState(null);
  const [archivingColumnId, setArchivingColumnId] = useState(null);

  // Obtener información del usuario actual
  const currentUserRole = localStorage.getItem('role');
  const currentUserEmail = localStorage.getItem('email');
  // Solo roles con permiso leads.archive (admin por defecto) ven archivar.
  const canArchive = hasPermission('leads.archive');

  const LEAD_CACHE_VERSION = 1;
  const LEAD_CACHE_TTL_MS = 2 * 60 * 1000; // 2 minutos
  const leadCacheKey = `bgl:leadBoard:v${LEAD_CACHE_VERSION}:${currentUserRole || 'unknown'}:${currentUserEmail || 'unknown'}`;

  const readLeadCache = () => {
    try {
      const raw = sessionStorage.getItem(leadCacheKey);
      if (!raw) return null;
      const parsed = JSON.parse(raw);
      if (!parsed || typeof parsed !== 'object') return null;
      if (!parsed.ts || !Array.isArray(parsed.data)) return null;
      if (Date.now() - parsed.ts > LEAD_CACHE_TTL_MS) return null;
      return parsed.data;
    } catch (e) {
      console.warn('[LeadBoard] Failed to read cache', e);
      return null;
    }
  };

  const writeLeadCache = (nextData) => {
    try {
      sessionStorage.setItem(
        leadCacheKey,
        JSON.stringify({ ts: Date.now(), data: nextData }),
      );
    } catch (e) {
      // Ignorar errores de quota / private mode
      console.warn('[LeadBoard] Failed to write cache', e);
    }
  };

  const toggleModal = (modalType, id = null) => {
    switch (modalType) {
      case 'leadDetail':
        if (id) setLeadToAssign(id);
        setLeadToDetail(!leadToDetail);
        break;
      case 'newLead':
        setNewLeadForm(!newLeadForm);
        break;
      case 'assigned':
        setAssignedForm(!assignedForm);
        break;
      case 'negociation':
        setNegociationForm(!negociationForm);
        break;
      case 'visit':
        if (id) setLeadToAssign(id);
        setVisitForm(!visitForm);
        break;
      case 'close':
        setCloseForm(!closeForm);
        break;
      case 'completed':
        setCompletedForm(!completedForm);
        break;
      default:
        break;
    }
  };

  const handleFormClose = (modalType, needsCleanup = false) => {
    if (originalData) {
      setData(originalData);
      setOriginalData(null);
    }

    if (needsCleanup) {
      setLeadToAssign(null);
      setDraggedCard(null);
      setLeadOrder(null);
    }

    toggleModal(modalType);
    window.location.reload();
  };

  const leadList = (options = {}) => {
    const { force = false, silent = false } = options;

    if (!silent) {
      setLoading(true);
    } else {
      setBackgroundRefreshing(true);
    }

    if (force) {
      try {
        sessionStorage.removeItem(leadCacheKey);
      } catch (e) {
        // ignore
      }
    }

    setError(null);
    dispatch(getLeads());
  };

  const transformBackendData = (backendData) => {
    const role = localStorage.getItem('role');
    const userEmail = localStorage.getItem('email');

    // Create the full board structure
    const fullBoardStructure = [
      {
        id: STATE_OPTIONS[0].value,
        title: STATE_OPTIONS[0].label,
        cards: [],
      },
      {
        id: STATE_OPTIONS[1].value,
        title: STATE_OPTIONS[1].label,
        cards: [],
      },
      {
        id: STATE_OPTIONS[2].value,
        title: STATE_OPTIONS[2].label,
        cards: [],
      },
      {
        id: STATE_OPTIONS[3].value,
        title: STATE_OPTIONS[3].label,
        cards: [],
      },
      {
        id: STATE_OPTIONS[4].value,
        title: STATE_OPTIONS[4].label,
        cards: [],
      },
      {
        id: STATE_OPTIONS[5].value,
        title: STATE_OPTIONS[5].label,
        cards: [],
      },
    ];

    // Filter columns based on user role
    // Hide "Nuevo" column (STATE_OPTIONS[0]) for vendedor role
    const boardStructure =
      role === 'vendedor'
        ? fullBoardStructure.filter((column) => column.id !== 'new')
        : fullBoardStructure;

    // Ensure backendData is an array
    if (!Array.isArray(backendData)) {
      console.warn('Backend data is not an array:', backendData);
      return boardStructure;
    }

    // Filtrar leads por rol y usuario asignado
    let filteredData = backendData;

    if (role === 'vendedor') {
      filteredData = backendData.filter(
        (lead) =>
          lead.data.assigned && lead.data.assigned.userEmail === userEmail,
      );
    }

    const transformedData = boardStructure.map((column) => {
      const cards = filteredData
        .filter((lead) => lead && lead.data && lead.data.state === column.id)
        .map((lead) => {
          try {
            return {
              id: lead.id,
              client: lead.data.clientInfo
                ? lead.data.clientInfo.useCompanyData === true &&
                  lead.data.clientInfo.companyInfo
                  ? lead.data.clientInfo.companyInfo.name
                  : `${lead.data.clientInfo.firstName} ${lead.data.clientInfo.lastName}`
                : '--',
              holding: lead.data.holdingInfo
                ? `${lead.data.holdingInfo.brand} ${lead.data.holdingInfo.model}`
                : '--',
              info: lead,
              order: lead.data[column.id]?.order || 0,
            };
          } catch (error) {
            console.error('Error processing lead:', lead, error);
            return {
              id: lead.id || 'unknown',
              client: '--',
              holding: '--',
              info: lead,
              order: 0,
            };
          }
        })
        .sort((a, b) => a.order - b.order);

      return {
        ...column,
        cards,
      };
    });

    return transformedData;
  };

  const handleDragStart = (e, cardId, columnId) => {
    setDraggedCard({ cardId, columnId });
    e.dataTransfer.setData('text/plain', JSON.stringify({ cardId, columnId }));
    e.dataTransfer.effectAllowed = 'move';
  };

  const handleDragOver = (e) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';
  };

  const handleDragEnter = (e, columnId, position) => {
    e.preventDefault();
    if (draggedCard) {
      setDragOverInfo({ columnId, position });
    }
  };

  const handleDragLeave = (e) => {
    e.preventDefault();
    // Only clear drag over info if we're leaving the drop zone entirely
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setDragOverInfo(null);
    }
  };

  // Nueva función para manejar drag over en toda la columna
  const handleColumnDragOver = (e, columnId) => {
    e.preventDefault();
    e.dataTransfer.dropEffect = 'move';

    if (!draggedCard) return;

    const columnElement = e.currentTarget;
    const rect = columnElement.getBoundingClientRect();
    const y = e.clientY - rect.top;
    const cardListElement = columnElement.querySelector(`.${styles.cardList}`);
    const cardListRect = cardListElement.getBoundingClientRect();
    const cardListTop = cardListRect.top - rect.top;

    // Calcular la posición relativa dentro de la lista de cards
    const relativeY = y - cardListTop;
    const cardHeight = 80; // Altura aproximada de cada card + margin
    const dropZoneHeight = 10; // Altura de las drop zones

    // Encontrar la columna actual
    const currentColumn = data.find((col) => col.id === columnId);
    if (!currentColumn) return;

    const totalCards = currentColumn.cards.length;
    let calculatedPosition = 0;

    // Si la columna está vacía
    if (totalCards === 0) {
      calculatedPosition = 0;
    } else {
      // Calcular posición basada en la posición del mouse
      calculatedPosition = Math.floor(
        relativeY / (cardHeight + dropZoneHeight),
      );
      calculatedPosition = Math.max(
        0,
        Math.min(calculatedPosition, totalCards),
      );
    }

    setColumnDragOver(columnId);
    setDropPosition(calculatedPosition);
    setDragOverInfo({ columnId, position: calculatedPosition });
  };

  // Nueva función para manejar drag leave de la columna
  const handleColumnDragLeave = (e) => {
    e.preventDefault();
    // Solo limpiar si realmente salimos de la columna
    if (!e.currentTarget.contains(e.relatedTarget)) {
      setColumnDragOver(null);
      setDropPosition(null);
      setDragOverInfo(null);
    }
  };

  const handleDrop = (e, targetColumnId, targetIndex) => {
    e.preventDefault();

    if (!draggedCard) return;

    const { cardId, columnId: sourceColumnId } = draggedCard;

    // Usar la posición calculada si está disponible
    const finalTargetIndex = dropPosition !== null ? dropPosition : targetIndex;

    // Save the original state before any changes
    setOriginalData([...data]);

    // Open the form based on the target column
    if (sourceColumnId !== targetColumnId) {
      // Calculate the order (position) for the new column
      setLeadOrder(finalTargetIndex);

      switch (targetColumnId) {
        case 'assigned':
          setLeadToAssign(cardId);
          setDraggedCard({ cardId, columnId: sourceColumnId, targetColumnId });
          setAssignedForm(true);
          break;
        case 'negotiation':
          setLeadToAssign(cardId);
          setDraggedCard({ cardId, columnId: sourceColumnId, targetColumnId });
          setNegociationForm(true);
          break;
        case 'visit':
          setLeadToAssign(cardId);
          setDraggedCard({ cardId, columnId: sourceColumnId, targetColumnId });
          setVisitForm(true);
          break;
        case 'close':
          setLeadToAssign(cardId);
          setDraggedCard({ cardId, columnId: sourceColumnId, targetColumnId });
          setCloseForm(true);
          break;
        case 'completed':
          setLeadToAssign(cardId);
          setDraggedCard({ cardId, columnId: sourceColumnId, targetColumnId });
          setCompletedForm(true);
          break;
        default:
          break;
      }
    }

    // Update the data
    const newData = [...data];
    const sourceColumnIndex = newData.findIndex(
      (col) => col.id === sourceColumnId,
    );
    const targetColumnIndex = newData.findIndex(
      (col) => col.id === targetColumnId,
    );

    // Get the source and target columns
    const sourceColumn = newData[sourceColumnIndex];
    const targetColumn = newData[targetColumnIndex];

    // If the card is in the same column, we only reorder it
    if (sourceColumnId === targetColumnId) {
      const newCards = [...sourceColumn.cards];
      const [removed] = newCards.splice(
        newCards.findIndex((card) => card.id === cardId),
        1,
      );
      newCards.splice(finalTargetIndex, 0, removed);
      newData[sourceColumnIndex] = {
        ...sourceColumn,
        cards: newCards,
      };

      // Update all affected cards in the backend with their new order
      newCards.forEach((card, index) => {
        const newOrder = index + 1; // Orders start from 1
        const currentOrder = card.order || 0;

        // Only update if the order actually changed
        if (newOrder !== currentOrder) {
          dispatch(updateLeadOrder(card.id, sourceColumnId, newOrder));
        }
      });
    } else {
      // If the card is in a different column, we move it to the target column
      const sourceCards = [...sourceColumn.cards];
      const targetCards = [...targetColumn.cards];
      const [removed] = sourceCards.splice(
        sourceCards.findIndex((card) => card.id === cardId),
        1,
      );
      targetCards.splice(finalTargetIndex, 0, removed);
      newData[sourceColumnIndex] = {
        ...sourceColumn,
        cards: sourceCards,
      };
      newData[targetColumnIndex] = {
        ...targetColumn,
        cards: targetCards,
      };
    }

    // Update the data
    setData(newData);
    setDraggedCard(null);
    setDragOverInfo(null);
    setColumnDragOver(null);
    setDropPosition(null);
  };

  // Nueva función para manejar drop en toda la columna
  const handleColumnDrop = (e, targetColumnId) => {
    e.preventDefault();

    if (!draggedCard) return;

    const finalTargetIndex = dropPosition !== null ? dropPosition : 0;
    handleDrop(e, targetColumnId, finalTargetIndex);
  };

  const getInitialLetter = (name) => {
    if (!name) return 'AS';
    const parts = name.trim().split(' ');
    if (parts.length === 0 || (parts.length === 1 && parts[0] === '')) {
      // Handle empty string or string with only spaces
      return 'AS';
    } else if (parts.length === 1) {
      // Handle cases with only one word
      const singleWord = parts[0];
      if (singleWord.length >= 2) {
        return singleWord.substring(0, 2).toUpperCase();
      } else {
        return singleWord.toUpperCase(); // Handles single character word
      }
    } else {
      // Handle cases with two or more words
      const initial1 = parts[0] && parts[0][0] ? parts[0][0].toUpperCase() : '';
      const initial2 = parts[1] && parts[1][0] ? parts[1][0].toUpperCase() : '';
      return `${initial1}${initial2}`;
    }
  };

  const generateVendorColor = (name) => {
    if (!name) return '#a8b2c1'; // Default soft gray color

    // Create a hash from the name
    let hash = 0;
    for (let i = 0; i < name.length; i++) {
      hash = name.charCodeAt(i) + ((hash << 5) - hash);
    }

    // Convert hash to soft RGB values
    const hue = Math.abs(hash) % 360;
    const saturation = 35 + (Math.abs(hash) % 15); // 35-50% (más suave)
    const lightness = 60 + (Math.abs(hash) % 20); // 60-80% (más claro)

    return `hsl(${hue}, ${saturation}%, ${lightness}%)`;
  };

  const downloadBlob = (blob, filename) => {
    const blobUrl = window.URL.createObjectURL(blob);
    const a = document.createElement('a');
    a.href = blobUrl;
    a.download = filename;
    document.body.appendChild(a);
    a.click();
    a.remove();
    window.URL.revokeObjectURL(blobUrl);
  };

  const parseFilenameFromContentDisposition = (contentDisposition) => {
    if (!contentDisposition || typeof contentDisposition !== 'string')
      return null;
    // Example: attachment; filename="leads.csv"
    const match = contentDisposition.match(/filename="?([^"]+)"?/i);
    return match && match[1] ? match[1] : null;
  };

  const tryReadBlobJsonMessage = async (maybeBlob) => {
    try {
      if (!(maybeBlob instanceof Blob)) return null;
      const text = await maybeBlob.text();
      if (!text) return null;
      const parsed = JSON.parse(text);
      return parsed?.message || null;
    } catch (e) {
      return null;
    }
  };

  const handleExportCsv = async () => {
    try {
      setExportingCsv(true);
      const res = await exportLeadsCsv();

      // If backend returned JSON error
      const contentType = res?.headers?.['content-type'] || '';
      if (res?.status >= 400) {
        const blobMessage = await tryReadBlobJsonMessage(res?.data);
        const message =
          blobMessage ||
          'No se pudo exportar el CSV (verifica permisos o intenta nuevamente).';
        setError(message);
        return;
      }

      const filenameFromHeader = parseFilenameFromContentDisposition(
        res?.headers?.['content-disposition'],
      );
      const today = new Date().toISOString().slice(0, 10);
      const filename = filenameFromHeader || `leads-${today}.csv`;

      // Axios with responseType blob gives a Blob in res.data
      const blob =
        res?.data instanceof Blob
          ? res.data
          : new Blob([res?.data], { type: contentType || 'text/csv' });

      downloadBlob(blob, filename);
    } catch (e) {
      console.error('[LeadBoard] Export CSV failed', e);
      setError('Error al exportar el CSV. Reintenta.');
    } finally {
      setExportingCsv(false);
    }
  };

  useEffect(() => {
    document.title = 'BGL - Tablero de Leads';
    // Pintar cache inmediatamente (si existe) y refrescar en background
    const cached = readLeadCache();
    if (cached) {
      setData(cached);
      setLoading(false);
      leadList({ silent: true });
      return;
    }

    leadList();
  }, []);

  useEffect(() => {
    if (leadState) {
      if (leadState.status === 'danger') {
        // Handle error case
        console.error('Error loading leads:', leadState.message);
        setError(leadState.message || 'Error al cargar los leads');
        setLoading(false);
        setBackgroundRefreshing(false);
        return;
      }

      if (leadState.data && Array.isArray(leadState.data)) {
        const transformedData = transformBackendData(leadState.data);
        setData(transformedData);
        writeLeadCache(transformedData);
        setError(null); // Clear any previous errors
      } else {
        // Handle case where data is null, undefined, or not an array
        console.warn(
          'Lead data is not available or not an array:',
          leadState.data,
        );
        setData([]);
        setError(null); // Clear errors when data is just empty
      }
      setLoading(false);
      setBackgroundRefreshing(false);
    }
  }, [leadState]);

  useEffect(() => {
    if (updateOrderState) {
      if (updateOrderState.status === 'danger') {
        console.error('Error updating lead order:', updateOrderState.message);
      } else if (updateOrderState.status === 'success') {
      }
    }
  }, [updateOrderState]);

  // Refrescar tablero tras archivar (u otro update de lead) para que desaparezca el archivado
  useEffect(() => {
    if (updateLeadState && updateLeadState.status === 'success') {
      leadList({ force: true });
    }
  }, [updateLeadState]);

  const handleArchiveAllInColumn = (column) => {
    if (!column?.cards?.length) return;
    setArchivingColumnId(column.id);
    const promises = column.cards.map((card) =>
      dispatch(updateLead(card.id, { archive: true })),
    );
    Promise.all(promises)
      .then(() => {
        leadList({ force: true });
      })
      .finally(() => {
        setArchivingColumnId(null);
      });
  };

  /**
   * Devuelve la clase del borde izquierdo tipo semáforo según antigüedad del lead:
   * < 24 h: verde, 24–48 h: amarillo, > 48 h: rojo
   */
  const getLeadBorderClass = (createdAt) => {
    if (!createdAt) return '';
    const created = new Date(createdAt).getTime();
    const hours = (Date.now() - created) / (1000 * 60 * 60);
    if (hours < 24) return styles.cardBorderGreen;
    if (hours < 48) return styles.cardBorderYellow;
    return styles.cardBorderRed;
  };

  const getClientLabel = (card) => {
    // Evita crasheos cuando backend devuelve datos parciales o durante cargas
    if (!card) {
      console.warn('[LeadBoard] card is null/undefined inside column.cards');
      return 'Cliente no disponible';
    }

    const clientInfo = card?.info?.data?.clientInfo;
    if (!clientInfo) {
      console.warn('[LeadBoard] clientInfo is null/undefined', {
        cardId: card?.id,
        holding: card?.holding,
        infoIsNull: card?.info == null,
        dataIsNull: card?.info?.data == null,
        clientInfo,
      });
      return 'Cliente no disponible';
    }

    const useCompanyData = clientInfo?.useCompanyData === true;
    if (useCompanyData) {
      return clientInfo?.companyInfo?.name || 'Empresa sin nombre';
    }

    const firstName = clientInfo?.firstName || '';
    const lastName = clientInfo?.lastName || '';
    const fullName = `${firstName} ${lastName}`.trim();
    return fullName || 'Cliente sin nombre';
  };

  return loading ? (
    <Loading />
  ) : error ? (
    <Container className={styles.leadContainer}>
      <Alert color="danger">
        <h4>Error al cargar leads</h4>
        <p>{error}</p>
        <Button
          color="primary"
          onClick={() => {
            setError(null);
            setLoading(true);
            leadList();
          }}
        >
          Reintentar
        </Button>
      </Alert>
    </Container>
  ) : (
    <>
      <Container className={styles.leadContainer}>
        <article>
          {/* Permitir crear leads tanto a admin como a vendedores */}
          {hasPermission('leads.export') && (
            <Button
              className="float-right mb-2"
              size="sm"
              disabled={exportingCsv}
              onClick={handleExportCsv}
            >
              <i className="fa fa-download mr-3"></i>
              <span>{exportingCsv ? 'Exportando...' : 'Exportar leads'}</span>
            </Button>
          )}
          {hasPermission('leads.create') && (
            <Button
              className="float-right mb-2"
              size="sm"
              onClick={() => toggleModal('newLead')}
            >
              <i className="fa fa-plus mr-3"></i>
              <span>Crear lead</span>
            </Button>
          )}
          <Button
            className="float-right mb-2"
            size="sm"
            onClick={() => leadList({ force: true })}
          >
            <i className="fa fa-sync mr-3"></i>
            <span>Refrescar</span>
          </Button>
          <h2>Tablero de Leads</h2>
        </article>
        <article className={styles.leadBoard}>
          <section className={styles.boardColumns}>
            {data &&
              Array.isArray(data) &&
              data.map((column) => (
                <section
                  key={column.id}
                  className={styles.column}
                  onDragOver={(e) => handleColumnDragOver(e, column.id)}
                  onDragLeave={handleColumnDragLeave}
                  onDrop={(e) => handleColumnDrop(e, column.id)}
                >
                  {/* Drop zone para toda la columna */}
                  <div
                    className={`${styles.columnDropZone} ${
                      columnDragOver === column.id ? styles.active : ''
                    }`}
                  />

                  {/* Indicador de posición de drop */}
                  {columnDragOver === column.id && dropPosition !== null && (
                    <div
                      className={styles.dropIndicator}
                      style={{
                        top: `${dropPosition * 105 + 60}px`, // Aproximadamente la posición calculada
                      }}
                    />
                  )}

                  <section className={styles.columnHeader}>
                    <h3>{column.title}</h3>
                    {canArchive &&
                      (column.id === 'close' || column.id === 'completed') &&
                      column.cards?.length > 0 && (
                        <div className={styles.columnHeaderActions}>
                          <button
                            type="button"
                            className={styles.archiveAllBtn}
                            onClick={(e) => {
                              e.stopPropagation();
                              handleArchiveAllInColumn(column);
                            }}
                            disabled={archivingColumnId === column.id}
                            title="Archivar todos los leads de esta columna"
                          >
                            <span className="fa fa-archive" />
                            {archivingColumnId === column.id
                              ? 'Archivando...'
                              : ''}
                          </button>
                        </div>
                      )}
                  </section>
                  <section className={styles.cardList}>
                    {column.cards && column.cards.length > 0 ? (
                      <>
                        {/* Drop zone at the beginning */}
                        <div
                          className={`${styles.dropZone} ${
                            dragOverInfo?.columnId === column.id &&
                            dragOverInfo?.position === 0
                              ? styles.dropZoneActive
                              : ''
                          }`}
                          onDragOver={handleDragOver}
                          onDragEnter={(e) => handleDragEnter(e, column.id, 0)}
                          onDragLeave={handleDragLeave}
                          onDrop={(e) => handleDrop(e, column.id, 0)}
                        />

                        {column.cards.map((card, cardIndex) => {
                          if (!card) {
                            console.warn(
                              '[LeadBoard] Null card found in column.cards',
                              { columnId: column?.id, cardIndex },
                            );
                            return (
                              <React.Fragment
                                key={`null-card-${column?.id}-${cardIndex}`}
                              >
                                {/* Drop zone after each card */}
                                <div
                                  className={`${styles.dropZone} ${
                                    dragOverInfo?.columnId === column.id &&
                                    dragOverInfo?.position === cardIndex + 1
                                      ? styles.dropZoneActive
                                      : ''
                                  }`}
                                  onDragOver={handleDragOver}
                                  onDragEnter={(e) =>
                                    handleDragEnter(e, column.id, cardIndex + 1)
                                  }
                                  onDragLeave={handleDragLeave}
                                  onDrop={(e) =>
                                    handleDrop(e, column.id, cardIndex + 1)
                                  }
                                />
                              </React.Fragment>
                            );
                          }

                          return (
                            <React.Fragment key={card.id}>
                              <section
                                className={`${styles.card} ${getLeadBorderClass(card?.info?.created_at)}`}
                                draggable
                                onDragStart={(e) =>
                                  handleDragStart(e, card.id, column.id)
                                }
                                onClick={() =>
                                  toggleModal('leadDetail', card.id)
                                }
                              >
                                {card?.info?.created_at && (
                                  <span className={styles.cardCreatedAt}>
                                    {dateFormat(card.info.created_at)}
                                  </span>
                                )}
                                <b>Cliente:</b> {getClientLabel(card)}
                                {card?.info?.data?.new?.source?.startsWith(
                                  'whatsapp',
                                ) && (
                                  <Badge
                                    color="success"
                                    className="ml-1"
                                    style={{ fontSize: '0.7em' }}
                                  >
                                    WhatsApp
                                  </Badge>
                                )}
                                <br />
                                <b>Bien:</b> {card.holding}
                                <section className={styles.actions}>
                                  {card?.info?.data?.state !== 'new' && (
                                    <section
                                      className={styles.assigned}
                                      title={card?.info?.data?.assigned?.name}
                                      style={{
                                        backgroundColor: generateVendorColor(
                                          card?.info?.data?.assigned?.name,
                                        ),
                                      }}
                                    >
                                      {getInitialLetter(
                                        card?.info?.data?.assigned?.name,
                                      )}
                                    </section>
                                  )}
                                  {card?.info?.data?.state === 'visit' && hasPermission('leads.edit') && (
                                    <button
                                      type="button"
                                      className={styles.cardIconBtn}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        toggleModal('visit', card.id);
                                      }}
                                      title="Editar visita"
                                    >
                                      <span className="fa fa-edit" />
                                    </button>
                                  )}
                                  {canArchive &&
                                    (column.id === 'close' ||
                                      column.id === 'completed') && (
                                    <button
                                      type="button"
                                      className={styles.cardIconBtn}
                                      onClick={(e) => {
                                        e.stopPropagation();
                                        dispatch(
                                          updateLead(card.id, {
                                            archive: true,
                                          }),
                                        );
                                      }}
                                      title="Archivar lead"
                                    >
                                      <span className="fa fa-archive" />
                                    </button>
                                  )}
                                </section>
                              </section>

                              {/* Drop zone after each card */}
                              <div
                                className={`${styles.dropZone} ${
                                  dragOverInfo?.columnId === column.id &&
                                  dragOverInfo?.position === cardIndex + 1
                                    ? styles.dropZoneActive
                                    : ''
                                }`}
                                onDragOver={handleDragOver}
                                onDragEnter={(e) =>
                                  handleDragEnter(e, column.id, cardIndex + 1)
                                }
                                onDragLeave={handleDragLeave}
                                onDrop={(e) =>
                                  handleDrop(e, column.id, cardIndex + 1)
                                }
                              />
                            </React.Fragment>
                          );
                        })}
                      </>
                    ) : (
                      <div
                        className={styles.emptyState}
                        onDragOver={handleDragOver}
                        onDragEnter={(e) => handleDragEnter(e, column.id, 0)}
                        onDragLeave={handleDragLeave}
                        onDrop={(e) => handleDrop(e, column.id, 0)}
                      >
                        No hay leads en esta etapa
                      </div>
                    )}
                  </section>
                </section>
              ))}
            {(!data || !Array.isArray(data) || data.length === 0) && (
              <div className={styles.emptyState}>
                <p>No hay datos de leads disponibles</p>
                <Button color="primary" onClick={() => leadList()}>
                  Cargar leads
                </Button>
              </div>
            )}
          </section>
        </article>

        {leadToDetail && (
          <LeadDetail
            leadId={leadToAssign}
            onSubmit={() => handleFormClose('leadDetail')}
            onCancel={() => handleFormClose('leadDetail')}
          />
        )}
        {newLeadForm && (
          <NewLeadForm
            onSubmit={() => handleFormClose('newLead')}
            onCancel={() => handleFormClose('newLead')}
          />
        )}
        {assignedForm && (
          <AssignedForm
            leadId={leadToAssign}
            leadOrder={leadOrder}
            onSubmit={() => handleFormClose('assigned', true)}
            onCancel={() => handleFormClose('assigned', true)}
          />
        )}
        {negociationForm && (
          <NegociationForm
            leadId={leadToAssign}
            leadOrder={leadOrder}
            onSubmit={() => handleFormClose('negociation', true)}
            onCancel={() => handleFormClose('negociation', true)}
          />
        )}
        {visitForm && (
          <VisitForm
            leadId={leadToAssign}
            leadOrder={leadOrder}
            onSubmit={() => handleFormClose('visit', true)}
            onCancel={() => handleFormClose('visit', true)}
          />
        )}
        {closeForm && (
          <CloseForm
            leadId={leadToAssign}
            leadOrder={leadOrder}
            onSubmit={() => handleFormClose('close', true)}
            onCancel={() => handleFormClose('close', true)}
          />
        )}
        {completedForm && (
          <CompletedForm
            leadId={leadToAssign}
            leadOrder={leadOrder}
            onSubmit={() => handleFormClose('completed', true)}
            onCancel={() => handleFormClose('completed', true)}
          />
        )}
      </Container>
    </>
  );
}

export default LeadBoard;
